import { useEffect, useState } from "react";
import { SpotNews, getSpotNewsData, SpotNewsConfig } from "@/data/spotNewsStore";
import { AlertCircle, ChevronRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export default function SpotNewsBanner() {
  const { lang, isRtl } = useI18n();
  const [news, setNews] = useState<SpotNews[]>([]);
  const [config, setConfig] = useState<SpotNewsConfig>({ isVisible: true, bannerTitle: "Skip pas!", speed: 60 });

  useEffect(() => {
    const loadData = async () => {
      const { news: allNews, config: loadedConfig } = await getSpotNewsData();
      setNews(allNews.filter(n => n.status === "published"));
      setConfig(loadedConfig);
    };

    loadData();
    window.addEventListener("kaynin_spot_news_updated", loadData);
    return () => {
      window.removeEventListener("kaynin_spot_news_updated", loadData);
    };
  }, []);

  const getNewsTitle = (item: SpotNews) => {
    if (lang === 'ar' && item.titleAr) return item.titleAr;
    if (lang === 'zgh' && item.titleZgh) return item.titleZgh;
    return item.title || "";
  };

  const getNewsContent = (item: SpotNews) => {
    if (lang === 'ar' && item.contentAr) return item.contentAr;
    if (lang === 'zgh' && item.contentZgh) return item.contentZgh;
    return item.content || "";
  };

  const validNews = news.filter(item => {
    const content = getNewsContent(item);
    return content && content.trim().length > 0;
  });

  if (validNews.length === 0 || !config.isVisible) return null;

  const displayBannerTitle = lang === 'ar' && config.bannerTitleAr ? config.bannerTitleAr : lang === 'zgh' && config.bannerTitleZgh ? config.bannerTitleZgh : config.bannerTitle;
  


  const viewText = lang === 'ar' ? 'شاهد' : lang === 'zgh' ? 'ⵥⵕ' : 'Voir';

  return (
    <div className="bg-gradient-to-r from-red-600/95 to-rose-600/95 text-white w-full md:max-w-4xl mx-auto py-2.5 px-3 sm:px-4 shadow-lg rounded-t-2xl relative flex items-center overflow-hidden border border-red-500/30 border-b-0 backdrop-blur-md" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className={`flex-shrink-0 flex items-center gap-1 sm:gap-2 font-bold uppercase tracking-wider text-[10px] sm:text-xs bg-white text-red-600 px-2 sm:px-3 py-1 rounded-full shadow-sm z-10 ${isRtl ? 'ml-2 sm:ml-4' : 'mr-2 sm:mr-4'}`}>
        <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 animate-pulse" />
        <span className="hidden sm:inline">{displayBannerTitle}</span>
      </div>
      <div className="flex-1 overflow-hidden relative h-6 flex items-center group">
        <div className={`flex shrink-0 min-w-full justify-around ${isRtl ? 'animate-marquee-rtl' : 'animate-marquee'} group-hover:[animation-play-state:paused]`} style={{ animationDuration: `${config.speed || 60}s` }}>
          {[...news, ...news, ...news, ...news].map((item, idx) => {
            const content = getNewsContent(item);
            return (
              <div key={`${item.id}-${idx}`} className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-8 whitespace-nowrap">
                {item.linkUrl ? (
                  <a href={item.linkUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 sm:gap-2 hover:underline">
                    <span className="font-bold text-yellow-300">{getNewsTitle(item)}{getNewsTitle(item) ? ' :' : ''}</span>
                    <span className="opacity-100 text-white/95">{content.length > 100 ? content.substring(0, 100) + '...' : content}</span>
                  </a>
                ) : (
                  <>
                    <span className="font-bold text-yellow-300">{getNewsTitle(item)}{getNewsTitle(item) ? ' :' : ''}</span>
                    <span className="opacity-100 text-white/95">{content.length > 100 ? content.substring(0, 100) + '...' : content}</span>
                  </>
                )}
                <span className={`text-white/30 ${isRtl ? 'mx-2 sm:mx-6' : 'mx-2 sm:mx-6'}`}>|</span>
              </div>
            );
          })}
        </div>
        <div className={`flex shrink-0 min-w-full justify-around ${isRtl ? 'animate-marquee-rtl' : 'animate-marquee'} group-hover:[animation-play-state:paused]`} aria-hidden="true" style={{ animationDuration: `${config.speed || 60}s` }}>
          {[...news, ...news, ...news, ...news].map((item, idx) => {
            const content = getNewsContent(item);
            return (
              <div key={`${item.id}-dup-${idx}`} className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-8 whitespace-nowrap">
                {item.linkUrl ? (
                  <a href={item.linkUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 sm:gap-2 hover:underline">
                    <span className="font-bold text-yellow-300">{getNewsTitle(item)}{getNewsTitle(item) ? ' :' : ''}</span>
                    <span className="opacity-100 text-white/95">{content.length > 100 ? content.substring(0, 100) + '...' : content}</span>
                  </a>
                ) : (
                  <>
                    <span className="font-bold text-yellow-300">{getNewsTitle(item)}{getNewsTitle(item) ? ' :' : ''}</span>
                    <span className="opacity-100 text-white/95">{content.length > 100 ? content.substring(0, 100) + '...' : content}</span>
                  </>
                )}
                <span className={`text-white/30 ${isRtl ? 'mx-2 sm:mx-6' : 'mx-2 sm:mx-6'}`}>|</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
